/**
 * Product Card Floating Actions & Quick Buy Modal Handler
 * Manages Quick View Modal (Tapasva.com design), AJAX Add to Cart, and LocalStorage Wishlist.
 */

const WISHLIST_STORAGE_KEY = 'storefront-wishlist';
const WISHLIST_ITEMS_KEY = 'storefront-wishlist-items';

let memoryWishlist = [];
let memoryWishlistItems = {};

/**
 * Reads wishlist product IDs from localStorage
 * @returns {string[]}
 */
export function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return memoryWishlist;
}

/**
 * Saves wishlist product IDs to localStorage
 * @param {string[]} list
 */
export function saveWishlist(list) {
  memoryWishlist = list || [];
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(memoryWishlist));
  } catch (e) {
    console.warn('[wishlist] Could not persist to localStorage:', e);
  }
}

/**
 * Reads wishlist item metadata from localStorage
 * @returns {Record<string, any>}
 */
export function getWishlistItemsData() {
  try {
    const raw = localStorage.getItem(WISHLIST_ITEMS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return memoryWishlistItems;
}

/**
 * Saves wishlist item metadata to localStorage
 * @param {Record<string, any>} items
 */
export function saveWishlistItemsData(items) {
  memoryWishlistItems = items || {};
  try {
    localStorage.setItem(WISHLIST_ITEMS_KEY, JSON.stringify(memoryWishlistItems));
  } catch (e) {
    console.warn('[wishlist] Could not persist item data:', e);
  }
}

/**
 * Updates all wishlist button states and header count across the document
 */
export function updateWishlistUI() {
  const wishlist = getWishlist();
  const buttons = document.querySelectorAll('.product-card-action--wishlist, .quick-buy-wishlist-btn, .product-pdp-wishlist-btn');

  buttons.forEach((btn) => {
    const productId = btn.dataset.productId;
    if (!productId) return;

    const isWishlisted = wishlist.includes(String(productId));
    btn.classList.toggle('is-wishlisted', isWishlisted);
    btn.classList.toggle('active', isWishlisted);
    btn.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');
    btn.setAttribute('title', isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist');
    btn.setAttribute('aria-label', (isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist') + ' - Product ' + productId);
  });

  // Update header and mobile bottom nav wishlist bubble and count
  const count = wishlist.length;
  const countBadges = document.querySelectorAll('.header-wishlist-count-badge, #header-wishlist-count, #mobile-wishlist-count-badge');
  const wishlistBubbles = document.querySelectorAll('#header-wishlist-bubble, .wishlist-bubble');
  const wishlistIcons = document.querySelectorAll('.header-actions__wishlist-icon');

  countBadges.forEach((badge) => {
    badge.textContent = String(count);
  });

  wishlistBubbles.forEach((bubble) => {
    bubble.style.display = count > 0 ? 'flex' : 'none';
  });

  wishlistIcons.forEach((icon) => {
    icon.classList.toggle('header-actions__wishlist-icon--has-wishlist', count > 0);
  });
}

/**
 * Toggles a product in the wishlist with optional product metadata
 * @param {string} productId
 * @param {any} [productData]
 */
export function toggleWishlistProduct(productId, productData) {
  if (!productId) return;
  const idStr = String(productId);
  let wishlist = getWishlist();
  let itemsData = getWishlistItemsData();
  const index = wishlist.indexOf(idStr);

  if (index > -1) {
    wishlist.splice(index, 1);
    delete itemsData[idStr];
  } else {
    wishlist.push(idStr);
    if (productData) {
      itemsData[idStr] = productData;
    }
  }

  saveWishlist(wishlist);
  saveWishlistItemsData(itemsData);
  updateWishlistUI();

  document.dispatchEvent(
    new CustomEvent('wishlist:update', {
      detail: { productId: idStr, wishlist },
      bubbles: true,
    })
  );
}

/**
 * Helper to format price in currency
 * @param {number} cents
 * @returns {string}
 */
function formatCurrency(cents) {
  if (typeof cents !== 'number') cents = Number(cents) || 0;
  const amount = (cents / 100).toFixed(2);
  return `Rs. ${amount}`;
}

/**
 * Shared helper: adds an item to cart via AJAX and updates cart-drawer + cart-icon
 * @param {number|string} variantId - The variant ID to add
 * @param {number} [quantity=1] - Quantity to add
 * @returns {Promise<any>}
 */
export async function addToCartWithDrawer(variantId, quantity = 1) {
  if (!variantId) throw new Error('No variant ID provided');

  // Collect section IDs needed by theme for cart drawer & cart items
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component, cart-drawer-component, [data-section-id]').forEach((el) => {
    if (el.dataset && el.dataset.sectionId) {
      sectionIds.add(el.dataset.sectionId);
    }
  });

  const cartDrawer = document.querySelector('theme-drawer[id*="cart"], cart-drawer-component, [id="cart-drawer"]');
  if (cartDrawer && cartDrawer.dataset && cartDrawer.dataset.sectionId) {
    sectionIds.add(cartDrawer.dataset.sectionId);
  }

  // Create CartLinesUpdateEvent promise first (so listeners attach before resolution)
  let deferredPromise = null;
  let CartLinesUpdateEventClass = null;

  try {
    const eventsModule = await import('@shopify/events');
    if (eventsModule && eventsModule.CartLinesUpdateEvent) {
      CartLinesUpdateEventClass = eventsModule.CartLinesUpdateEvent;
      deferredPromise = CartLinesUpdateEventClass.createPromise();

      document.dispatchEvent(
        new CartLinesUpdateEventClass({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: Number(quantity) || 1 }],
          promise: deferredPromise.promise,
        })
      );
    }
  } catch (e) {
    console.warn('[product-card-actions] Could not import @shopify/events:', e);
  }

  // Prepare request body with sections for native DOM morphing
  const payload = {
    id: Number(variantId) || variantId,
    quantity: Number(quantity) || 1,
  };
  if (sectionIds.size > 0) {
    payload.sections = Array.from(sectionIds).join(',');
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (deferredPromise) {
      deferredPromise.reject(new Error(errorData.description || 'Add to cart failed'));
    }
    throw new Error(errorData.description || errorData.message || 'Failed to add item to cart');
  }

  const data = await response.json();

  // Fetch full updated cart to ensure complete cart state & currency for @shopify/events
  const fullCart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
  const activeCurrency = fullCart?.currency || data?.currency || window.Shopify?.currency?.active || window.Shopify?.currency?.current_currency || 'INR';

  const cartObj = fullCart || {
    ...data,
    currency: activeCurrency,
    items: data.items || (data.id ? [data] : []),
    item_count: data.item_count || 1,
    total_price: data.final_line_price || data.price || 0,
  };
  if (!cartObj.currency) cartObj.currency = activeCurrency;
  if (Array.isArray(cartObj.items)) {
    cartObj.items.forEach((item) => {
      if (!item.currency) item.currency = activeCurrency;
    });
  }

  // Resolve CartLinesUpdateEvent if active
  if (deferredPromise && CartLinesUpdateEventClass) {
    try {
      const parsedCart = CartLinesUpdateEventClass.createCartFromAjaxResponse(cartObj);
      deferredPromise.resolve({
        cart: parsedCart,
        detail: {
          sections: data.sections,
          items: cartObj.items,
          itemCount: cartObj.item_count,
          source: 'product-card-actions',
          didError: false,
        },
      });
    } catch (e) {
      console.warn('[product-card-actions] createCartFromAjaxResponse notice:', e);
      deferredPromise.resolve({
        cart: cartObj,
        detail: {
          sections: data.sections,
          items: cartObj.items,
          itemCount: cartObj.item_count,
          source: 'product-card-actions',
          didError: false,
        },
      });
    }
  }

  // Fallback: manually morph section HTML if returned
  if (data.sections) {
    Object.entries(data.sections).forEach(([secId, html]) => {
      const target = document.querySelector(`[data-section-id="${secId}"]`);
      if (target && html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newEl = parsed.querySelector(`[data-section-id="${secId}"]`) || parsed.body.firstElementChild;
        if (newEl) {
          target.innerHTML = newEl.innerHTML;
        }
      }
    });
  }

  // Update header and mobile cart count badges
  const newCount = cartObj.item_count ?? (await fetch('/cart.js').then((r) => r.json()).then((c) => c.item_count).catch(() => null));
  if (typeof newCount === 'number') {
    document.querySelectorAll('.cart-count, .header-actions__cart-icon-bubble, #header-cart-count, cart-icon [ref="cartCount"], [data-cart-count], #mobile-cart-count-badge').forEach((badge) => {
      badge.textContent = String(newCount);
      badge.classList.toggle('hidden', newCount === 0);
    });
  }

  // Auto-open theme cart drawer
  setTimeout(() => {
    const themeDrawer = document.querySelector('theme-drawer[id*="cart"], cart-drawer-component, #cart-drawer');
    if (themeDrawer) {
      if (typeof themeDrawer.open === 'function') {
        themeDrawer.open();
      } else {
        const parentDrawer = themeDrawer.closest('theme-drawer');
        if (parentDrawer && typeof parentDrawer.open === 'function') {
          parentDrawer.open();
        }
      }
    }
  }, 120);

  return data;
}

/**
 * Handles adding a product card variant to the cart via AJAX
 * @param {HTMLButtonElement} button
 */
async function handleAddToCart(button) {
  const productCard = button.closest('product-card, .product-card, .card, [data-product-card]');
  let variantId = button.dataset.variantId;

  if (productCard) {
    const selectedInput = productCard.querySelector('input[data-variant-id]:checked, input[name="id"]');
    if (selectedInput && selectedInput.value) {
      variantId = selectedInput.value;
    }
  }

  if (!variantId) {
    console.warn('[product-card-actions] No variant ID found for Add to Cart');
    return;
  }

  button.disabled = true;
  button.classList.add('is-loading');

  try {
    await addToCartWithDrawer(variantId, 1);

    button.classList.remove('is-loading');
    button.classList.add('is-success');

    setTimeout(() => {
      button.classList.remove('is-success');
      button.disabled = false;
    }, 1500);
  } catch (error) {
    console.error('[product-card-actions] Add to cart error:', error);
    button.classList.remove('is-loading');
    button.disabled = false;
  }
}

/**
 * Opens and renders the Quick Buy modal for a product
 * @param {HTMLButtonElement} button
 */
async function handleQuickView(button) {
  let productUrl = button.dataset.productUrl;
  if (!productUrl) return;

  const dialogComponent = document.getElementById('quick-add-dialog');
  const modalContent = document.getElementById('quick-buy-modal-content');

  if (!dialogComponent || !modalContent) {
    window.location.href = productUrl;
    return;
  }

  // Open modal with loading state
  modalContent.innerHTML = `
    <div class="quick-buy-loader">
      <div class="quick-buy-spinner"></div>
    </div>
  `;

  if (typeof dialogComponent.showDialog === 'function') {
    dialogComponent.showDialog();
  } else {
    const dialog = dialogComponent.querySelector('dialog');
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
  }

  try {
    // Extract handle from productUrl
    const urlObj = new URL(productUrl, window.location.origin);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    const productHandle = pathParts[pathParts.length - 1];

    const jsonUrl = `/products/${productHandle}.js`;
    const response = await fetch(jsonUrl);
    if (!response.ok) {
      throw new Error(`Failed to load product JSON: ${response.status}`);
    }

    const product = await response.json();
    renderQuickBuyModal(product, modalContent, dialogComponent);
  } catch (err) {
    console.error('[quick-buy] Failed to render modal, falling back to page:', err);
    window.location.href = productUrl;
  }
}

/**
 * Renders the Quick Buy Modal HTML and attaches event listeners
 * @param {any} product - Shopify product JSON
 * @param {HTMLElement} container - Modal content container
 * @param {HTMLElement} dialogComponent - Quick add dialog element
 */
function renderQuickBuyModal(product, container, dialogComponent) {
  const productUrl = product.url || `/products/${product.handle}`;
  let currentVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
  let currentQuantity = 1;
  const images = product.images && product.images.length > 0 ? product.images : [product.featured_image || ''];
  let activeImageIndex = 0;

  const vendorName = product.vendor || 'TAPASVA RITUALS';
  const price = currentVariant ? currentVariant.price : product.price;
  const comparePrice = currentVariant ? currentVariant.compare_at_price : product.compare_at_price;
  const hasDiscount = comparePrice && comparePrice > price;
  const discountPercent = hasDiscount ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;
  const isAvailable = currentVariant ? currentVariant.available : product.available;

  const hasMultipleVariants = product.variants && product.variants.length > 1;
  const isWishlisted = getWishlist().includes(String(product.id));

  // Build Thumbnail list HTML
  const thumbsHtml = images
    .map(
      (img, idx) => `
      <div class="quick-buy-thumb-item ${idx === 0 ? 'is-active' : ''}" data-index="${idx}">
        <img src="${img}" alt="${product.title} ${idx + 1}" loading="lazy" width="60" height="60" />
      </div>
    `
    )
    .join('');

  // Build Variant Pills HTML
  let variantsHtml = '';
  if (hasMultipleVariants) {
    const pills = product.variants
      .map(
        (v) => `
        <button 
          type="button" 
          class="quick-buy-pill ${v.id === currentVariant.id ? 'is-active' : ''} ${!v.available ? 'is-disabled' : ''}"
          data-variant-id="${v.id}"
          data-variant-title="${v.title}"
          data-variant-price="${v.price}"
          data-variant-compare="${v.compare_at_price || ''}"
          data-variant-available="${v.available}"
          data-variant-image="${v.featured_image ? v.featured_image.src : ''}"
        >
          ${v.title}
        </button>
      `
      )
      .join('');

    variantsHtml = `
      <div class="quick-buy-variants-label">Variants : <span class="quick-buy-selected-variant-text">${currentVariant.title}</span></div>
      <div class="quick-buy-pills-row">
        ${pills}
      </div>
    `;
  }

  container.innerHTML = `
    <button type="button" class="quick-buy-close-btn" aria-label="Close modal">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>

    <div class="quick-buy-container">
      <!-- Left Column: Gallery -->
      <div class="quick-buy-gallery">
        <div class="quick-buy-main-image-wrap">
          <img class="quick-buy-main-image" src="${images[0]}" alt="${product.title}" width="400" height="400" />
        </div>

        ${
          images.length > 1
            ? `
          <div class="quick-buy-thumbs-wrap">
            <button type="button" class="quick-buy-nav-arrow quick-buy-nav-prev" aria-label="Previous image">&#10094;</button>
            <div class="quick-buy-thumbs-list">
              ${thumbsHtml}
            </div>
            <button type="button" class="quick-buy-nav-arrow quick-buy-nav-next" aria-label="Next image">&#10095;</button>
          </div>
        `
            : ''
        }
      </div>

      <!-- Right Column: Product Information & Purchasing -->
      <div class="quick-buy-info">
        <div>
          <div class="quick-buy-vendor">🌿 ${vendorName || 'SUNDAR FOODS'}</div>
          <h2 class="quick-buy-title">${product.title}</h2>

          <div class="quick-buy-price-row">
            <span class="quick-buy-price">${formatCurrency(price)}</span>
            <span class="quick-buy-compare-price" style="${hasDiscount ? '' : 'display:none;'}">${formatCurrency(comparePrice || 0)}</span>
            <span class="quick-buy-discount-badge" style="${hasDiscount ? '' : 'display:none;'}">🔥 ${discountPercent}% OFF</span>
          </div>

          <div class="quick-buy-tax-note">Tax included. Shipping calculated at checkout.</div>

          <div class="quick-buy-trust-bar">
            <span class="quick-buy-trust-pill">🌿 100% Natural</span>
            <span class="quick-buy-trust-pill">🚚 Fast Dispatch</span>
            <span class="quick-buy-trust-pill">🔒 Safe & Secure</span>
          </div>

          ${variantsHtml}

          <div class="quick-buy-qty-row">
            <div class="quick-buy-qty-label">Quantity :</div>
            <div class="quick-buy-stepper">
              <button type="button" class="quick-buy-stepper-minus" aria-label="Decrease quantity">&minus;</button>
              <span class="quick-buy-stepper-val">1</span>
              <button type="button" class="quick-buy-stepper-plus" aria-label="Increase quantity">&plus;</button>
            </div>

            <button 
              type="button" 
              class="quick-buy-wishlist-btn ${isWishlisted ? 'is-wishlisted' : ''}" 
              data-product-id="${product.id}"
              aria-label="Toggle Wishlist"
              title="Add to Wishlist"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>

          <div class="quick-buy-ctas-row">
            <button type="button" class="quick-buy-cta quick-buy-add-to-cart">
              <svg class="quick-buy-icon-bag" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                <path d="M3 6h18"></path>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
              <span>Add to Cart</span>
            </button>

            <button type="button" class="quick-buy-cta quick-buy-buy-now">
              <svg class="quick-buy-icon-bolt" viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
                <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"></path>
              </svg>
              <span>Buy It Now</span>
            </button>
          </div>

          <a href="${productUrl}${currentVariant ? `?variant=${currentVariant.id}` : ''}" class="quick-buy-view-details-btn">
            <span>View Full Details</span>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </a>
        </div>
      </div>
    </div>
  `;

  // Close Button Logic
  const closeBtn = container.querySelector('.quick-buy-close-btn');
  const closeModal = () => {
    if (typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    } else {
      const dialog = dialogComponent.querySelector('dialog');
      if (dialog && typeof dialog.close === 'function') {
        dialog.close();
      }
    }
  };
  closeBtn?.addEventListener('click', closeModal);

  // Gallery Navigation
  const mainImg = container.querySelector('.quick-buy-main-image');
  const thumbItems = container.querySelectorAll('.quick-buy-thumb-item');
  const thumbsList = container.querySelector('.quick-buy-thumbs-list');
  const prevBtn = container.querySelector('.quick-buy-nav-prev');
  const nextBtn = container.querySelector('.quick-buy-nav-next');

  const updateActiveImage = (idx) => {
    activeImageIndex = idx;
    if (mainImg) mainImg.src = images[idx];
    thumbItems.forEach((t, i) => t.classList.toggle('is-active', i === idx));
  };

  thumbItems.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const idx = Number(thumb.dataset.index) || 0;
      updateActiveImage(idx);
    });
  });

  prevBtn?.addEventListener('click', () => {
    const newIdx = (activeImageIndex - 1 + images.length) % images.length;
    updateActiveImage(newIdx);
    if (thumbsList) thumbsList.scrollBy({ left: -60, behavior: 'smooth' });
  });

  nextBtn?.addEventListener('click', () => {
    const newIdx = (activeImageIndex + 1) % images.length;
    updateActiveImage(newIdx);
    if (thumbsList) thumbsList.scrollBy({ left: 60, behavior: 'smooth' });
  });

  // Variant Pills Switching
  const variantPills = container.querySelectorAll('.quick-buy-pill');
  const selectedVariantText = container.querySelector('.quick-buy-selected-variant-text');
  const priceElem = container.querySelector('.quick-buy-price');
  const comparePriceElem = container.querySelector('.quick-buy-compare-price');
  const discountBadgeElem = container.querySelector('.quick-buy-discount-badge');
  const atcButton = container.querySelector('.quick-buy-add-to-cart');
  const buyNowButton = container.querySelector('.quick-buy-buy-now');

  variantPills.forEach((pill) => {
    pill.addEventListener('click', () => {
      const vId = pill.dataset.variantId;
      const foundVariant = product.variants.find((v) => String(v.id) === String(vId));
      if (!foundVariant) return;

      currentVariant = foundVariant;
      variantPills.forEach((p) => p.classList.remove('is-active'));
      pill.classList.add('is-active');

      if (selectedVariantText) selectedVariantText.textContent = foundVariant.title;

      // Update price and discount
      const vPrice = foundVariant.price;
      const vCompare = foundVariant.compare_at_price;
      const vDiscount = vCompare && vCompare > vPrice;
      const vPercent = vDiscount ? Math.round(((vCompare - vPrice) / vCompare) * 100) : 0;

      if (priceElem) priceElem.textContent = formatCurrency(vPrice);
      if (comparePriceElem) {
        comparePriceElem.textContent = formatCurrency(vCompare || 0);
        comparePriceElem.style.display = vDiscount ? '' : 'none';
      }
      if (discountBadgeElem) {
        discountBadgeElem.textContent = `${vPercent}% OFF`;
        discountBadgeElem.style.display = vDiscount ? '' : 'none';
      }

      // Update availability
      if (atcButton) {
        atcButton.disabled = false;
        const atcSpan = atcButton.querySelector('span');
        if (atcSpan) atcSpan.textContent = 'ADD TO CART';
      }
      if (buyNowButton) {
        buyNowButton.disabled = false;
      }

      // Update image if variant has one
      if (foundVariant.featured_image && foundVariant.featured_image.src) {
        if (mainImg) mainImg.src = foundVariant.featured_image.src;
      }

      // Update View Full Details redirect link with selected variant
      const viewDetailsBtn = container.querySelector('.quick-buy-view-details-btn');
      if (viewDetailsBtn) {
        viewDetailsBtn.href = `${productUrl}?variant=${foundVariant.id}`;
      }
    });
  });

  // Quantity Stepper
  const minusBtn = container.querySelector('.quick-buy-stepper-minus');
  const plusBtn = container.querySelector('.quick-buy-stepper-plus');
  const qtyVal = container.querySelector('.quick-buy-stepper-val');

  minusBtn?.addEventListener('click', () => {
    if (currentQuantity > 1) {
      currentQuantity--;
      if (qtyVal) qtyVal.textContent = String(currentQuantity);
    }
  });

  plusBtn?.addEventListener('click', () => {
    currentQuantity++;
    if (qtyVal) qtyVal.textContent = String(currentQuantity);
  });

  // Wishlist Button
  const wishlistBtn = container.querySelector('.quick-buy-wishlist-btn');
  wishlistBtn?.addEventListener('click', () => {
    toggleWishlistProduct(String(product.id));
  });

  // Add to Cart in Quick View Modal
  atcButton?.addEventListener('click', async () => {
    if (!currentVariant || !currentVariant.available) return;

    atcButton.disabled = true;
    atcButton.classList.add('is-loading');
    const atcSpan = atcButton.querySelector('span');
    const originalText = atcSpan ? atcSpan.textContent : 'ADD TO CART';
    if (atcSpan) atcSpan.textContent = 'ADDING...';

    try {
      await addToCartWithDrawer(currentVariant.id, currentQuantity);
      if (atcSpan) atcSpan.textContent = 'ADDED! ✓';

      setTimeout(() => {
        closeModal();
      }, 400);
    } catch (err) {
      console.error('[quick-buy] Add to cart error:', err);
      if (atcSpan) atcSpan.textContent = originalText;
    } finally {
      setTimeout(() => {
        atcButton.classList.remove('is-loading');
        atcButton.disabled = false;
        if (atcSpan && atcSpan.textContent === 'ADDING...') {
          atcSpan.textContent = originalText;
        }
      }, 500);
    }
  });

  // Buy It Now
  buyNowButton?.addEventListener('click', async () => {
    if (!currentVariant || !currentVariant.available) return;

    buyNowButton.disabled = true;
    buyNowButton.classList.add('is-loading');

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: currentVariant.id,
          quantity: currentQuantity,
        }),
      });

      if (!resp.ok) throw new Error('Add to cart failed for checkout');

      window.location.href = '/checkout';
    } catch (err) {
      console.error('[quick-buy] Buy now error:', err);
      buyNowButton.classList.remove('is-loading');
      buyNowButton.disabled = false;
    }
  });
}

/**
 * Opens and renders the Wishlist Drawer
 */
export async function openWishlistDrawer() {
  let drawer = document.getElementById('storefront-wishlist-drawer');
  if (!drawer) {
    drawer = document.createElement('div');
    drawer.id = 'storefront-wishlist-drawer';
    drawer.className = 'storefront-wishlist-drawer';
    drawer.innerHTML = `
      <div class="wishlist-drawer__backdrop"></div>
      <div class="wishlist-drawer__panel">
        <div class="wishlist-drawer__header">
          <div class="wishlist-drawer__title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <span>My Wishlist (<span class="wishlist-drawer__count">0</span>)</span>
          </div>
          <button type="button" class="wishlist-drawer__close" aria-label="Close Wishlist">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="wishlist-drawer__body">
          <div class="wishlist-drawer__loading">
            <div class="quick-buy-spinner"></div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(drawer);

    // Event listeners for close
    drawer.querySelector('.wishlist-drawer__backdrop')?.addEventListener('click', closeWishlistDrawer);
    drawer.querySelector('.wishlist-drawer__close')?.addEventListener('click', closeWishlistDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer?.classList.contains('is-open')) {
        closeWishlistDrawer();
      }
    });
  }

  drawer.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  renderWishlistDrawerItems();
}

/**
 * Closes the Wishlist Drawer
 */
export function closeWishlistDrawer() {
  const drawer = document.getElementById('storefront-wishlist-drawer');
  if (drawer) {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

/**
 * Renders the products currently in the wishlist inside the drawer
 */
async function renderWishlistDrawerItems() {
  const drawer = document.getElementById('storefront-wishlist-drawer');
  if (!drawer) return;
  const body = drawer.querySelector('.wishlist-drawer__body');
  const countEl = drawer.querySelector('.wishlist-drawer__count');
  const wishlist = getWishlist();

  if (countEl) countEl.textContent = String(wishlist.length);

  if (wishlist.length === 0) {
    if (body) {
      body.innerHTML = `
        <div class="wishlist-drawer__empty">
          <div class="wishlist-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>
          <p class="wishlist-empty-title">Your wishlist is empty</p>
          <p class="wishlist-empty-subtitle">Save items you love to find them easily later.</p>
          <a href="/collections/all" class="wishlist-empty-btn" onclick="document.getElementById('storefront-wishlist-drawer')?.classList.remove('is-open');">Explore Catalog</a>
        </div>
      `;
    }
    return;
  }

  // Load product details
  try {
    const itemsData = getWishlistItemsData();
    let products = [];

    // Try fetching products if needed
    const fetchPromises = wishlist.map(async (id) => {
      if (itemsData[id] && itemsData[id].title) return itemsData[id];
      try {
        const resp = await fetch(`/search/suggest.json?q=${id}&resources[type]=product&resources[limit]=1`);
        if (resp.ok) {
          const data = await resp.json();
          const p = data?.resources?.results?.products?.[0];
          if (p) return p;
        }
      } catch (e) {}
      return { id, title: 'Wishlist Product #' + id, price: '', url: '/products' };
    });

    products = await Promise.all(fetchPromises);

    if (body) {
      body.innerHTML = `
        <div class="wishlist-drawer__list">
          ${products
            .map((item, idx) => {
              const id = wishlist[idx] || item.id;
              const imgUrl = item.image || item.featured_image || item.featured_image?.url || '';
              const title = item.title || 'Product';
              const price = item.price ? (typeof item.price === 'number' ? `Rs. ${(item.price / 100).toFixed(2)}` : item.price) : '';
              const url = item.url || `/collections/all`;
              const variantId = item.variants?.[0]?.id || item.variant_id || id;

              return `
                <div class="wishlist-drawer-item" data-product-id="${id}">
                  <div class="wishlist-item__image">
                    ${imgUrl ? `<img src="${imgUrl}" alt="${title}" loading="lazy" width="80" height="80" />` : `<div class="wishlist-item__no-image">📦</div>`}
                  </div>
                  <div class="wishlist-item__info">
                    <a href="${url}" class="wishlist-item__title">${title}</a>
                    ${price ? `<div class="wishlist-item__price">${price}</div>` : ''}
                    <div class="wishlist-item__actions">
                      <button type="button" class="wishlist-item__add-btn" data-variant-id="${variantId}">Add to Cart</button>
                      <button type="button" class="wishlist-item__remove-btn" data-product-id="${id}" aria-label="Remove item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              `;
            })
            .join('')}
        </div>
      `;

      // Attach remove item listeners
      body.querySelectorAll('.wishlist-item__remove-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const target = /** @type {HTMLElement} */ (e.currentTarget);
          const pId = target.dataset.productId;
          if (pId) {
            toggleWishlistProduct(pId);
            renderWishlistDrawerItems();
          }
        });
      });

      // Attach add to cart listeners
      body.querySelectorAll('.wishlist-item__add-btn').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          const b = /** @type {HTMLButtonElement} */ (e.currentTarget);
          const vId = b.dataset.variantId;
          if (!vId) return;

          b.textContent = 'Adding...';
          b.disabled = true;

          try {
            await addToCartWithDrawer(vId, 1);
            b.textContent = 'Added! ✓';
            b.style.backgroundColor = '#15803d';

            setTimeout(() => {
              b.textContent = 'Add to Cart';
              b.style.backgroundColor = '#02431b';
              b.disabled = false;
            }, 1500);
          } catch (err) {
            console.error('[wishlist] add error:', err);
            b.textContent = 'View Product';
            b.disabled = false;
          }
        });
      });
    }
  } catch (e) {
    console.error('[wishlist] render error:', e);
  }
}

/**
 * Initializes global event delegation for floating action buttons
 */
function initActionsDelegation() {
  document.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement | null} */ (e.target);
    if (!target) return;

    // Header wishlist button trigger
    const wishlistTrigger = target.closest('[data-wishlist-trigger], #header-wishlist-trigger');
    if (wishlistTrigger) {
      e.preventDefault();
      openWishlistDrawer();
      return;
    }

    // PDP wishlist button
    const pdpWishlist = target.closest('.product-pdp-wishlist-btn');
    if (pdpWishlist) {
      e.preventDefault();
      const productId = pdpWishlist.dataset.productId || window.location.pathname.split('/').pop();
      const metadata = {
        id: productId,
        title: pdpWishlist.dataset.productTitle || document.querySelector('.product-title, h1')?.textContent?.trim() || 'Product',
        image: pdpWishlist.dataset.productImage || document.querySelector('.product-media img, .product__media img')?.src || '',
        price: pdpWishlist.dataset.productPrice || document.querySelector('.price, .product-price')?.textContent?.trim() || '',
        url: pdpWishlist.dataset.productUrl || window.location.pathname,
        variant_id: pdpWishlist.dataset.variantId || productId,
      };
      if (productId) {
        toggleWishlistProduct(productId, metadata);
      }
      return;
    }

    // Product card actions (floating circular actions + bottom buttons)
    const actionBtn = target.closest('.product-card-action, .product-card-bottom-btn');
    if (!actionBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const action = actionBtn.dataset.action;

    if (action === 'wishlist') {
      const productId = actionBtn.dataset.productId;
      const card = actionBtn.closest('product-card, .product-card, .card, [data-product-card]');
      const metadata = {
        id: productId,
        title: actionBtn.dataset.productTitle || card?.querySelector('.product-title, .product-card__title, h2, h3, a')?.textContent?.trim() || 'Product',
        image: actionBtn.dataset.productImage || card?.querySelector('img')?.src || '',
        price: actionBtn.dataset.productPrice || card?.querySelector('.price, .product-card__price')?.textContent?.trim() || '',
        url: actionBtn.dataset.productUrl || card?.querySelector('a')?.href || '/products',
        variant_id: actionBtn.dataset.variantId || productId,
      };
      toggleWishlistProduct(productId, metadata);
    } else if (action === 'add-to-cart') {
      handleAddToCart(/** @type {HTMLButtonElement} */ (actionBtn));
    } else if (action === 'quick-view') {
      handleQuickView(/** @type {HTMLButtonElement} */ (actionBtn));
    }
  });

  // Keep wishlist button states synchronized
  updateWishlistUI();

  // Re-sync on dynamic section loads and facet updates
  document.addEventListener('shopify:section:load', updateWishlistUI);
  document.addEventListener('wishlist:update', updateWishlistUI);
}

// Expose global window methods for live theme scripts & inline click handlers
if (typeof window !== 'undefined') {
  window.getWishlist = getWishlist;
  window.saveWishlist = saveWishlist;
  window.getWishlistItemsData = getWishlistItemsData;
  window.saveWishlistItemsData = saveWishlistItemsData;
  window.updateWishlistUI = updateWishlistUI;
  window.toggleWishlistProduct = toggleWishlistProduct;
  window.openWishlistDrawer = openWishlistDrawer;
  window.closeWishlistDrawer = closeWishlistDrawer;
}

// Execute delegation immediately and on DOMContentLoaded / pageshow
initActionsDelegation();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initActionsDelegation);
}
window.addEventListener('pageshow', updateWishlistUI);
